import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminGuard from '../../components/admin/AdminGuard';
import { fetchContact, updateContact } from '../../services/contactService';

const EMPTY_LINK = () => ({ label: '', href: '' });

export default function AdminContactPage() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: '', title: '', email: '', location: '', intro: '',
    services: [''], links: [EMPTY_LINK()],
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    fetchContact().then((data) => {
      if (!data) { setNotFound(true); setLoading(false); return; }
      setForm({
        name: data.name || '',
        title: data.title || '',
        email: data.email || '',
        location: data.location || '',
        intro: data.intro || '',
        services: data.services?.length ? data.services : [''],
        links: data.links?.length ? data.links : [EMPTY_LINK()],
      });
      setLoading(false);
    });
  }, []);

  const sp = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const updateService = (i, val) =>
    setForm((f) => ({ ...f, services: f.services.map((s, idx) => idx === i ? val : s) }));
  const addService = () => setForm((f) => ({ ...f, services: [...f.services, ''] }));
  const removeService = (i) =>
    setForm((f) => ({ ...f, services: f.services.filter((_, idx) => idx !== i) }));

  const updateLink = (i, field, val) =>
    setForm((f) => ({ ...f, links: f.links.map((l, idx) => idx === i ? { ...l, [field]: val } : l) }));
  const addLink = () => setForm((f) => ({ ...f, links: [...f.links, EMPTY_LINK()] }));
  const removeLink = (i) =>
    setForm((f) => ({ ...f, links: f.links.filter((_, idx) => idx !== i) }));

  const handleSave = async () => {
    setSaving(true); setError('');
    try {
      await updateContact({
        name: form.name,
        title: form.title,
        email: form.email,
        location: form.location,
        intro: form.intro,
        services: form.services.map((s) => s.trim()).filter(Boolean),
        links: form.links.filter((l) => l.label.trim() && l.href.trim()),
      });
      navigate('/admin/dashboard');
    } catch (e) {
      setError(e.message);
      setSaving(false);
    }
  };

  return (
    <AdminGuard>
      <div className="admin-page">
        <header className="admin-header">
          <button onClick={() => navigate('/admin/dashboard')} className="admin-btn-ghost">← 목록</button>
          <span className="admin-header-title">Contact 페이지 편집</span>
          <button onClick={handleSave} disabled={saving || loading || notFound} className="admin-btn-primary">
            {saving ? '저장 중…' : '저장'}
          </button>
        </header>

        <main className="admin-main">
          {error && <p className="admin-error">{error}</p>}

          {loading ? (
            <p className="admin-loading">불러오는 중...</p>
          ) : notFound ? (
            <section className="admin-form-section">
              <p className="admin-field-hint">
                아직 <code>site_contact</code> 테이블이 없어서 Contact 콘텐츠를 관리자 페이지에서
                편집할 수 없어요. Supabase SQL Editor에서 테이블을 먼저 만들어주세요.
              </p>
            </section>
          ) : (
            <>
              <section className="admin-form-section">
                <h3 className="admin-form-section-title">기본 정보</h3>
                <div className="admin-form-grid">
                  <label className="admin-label">이름
                    <input className="admin-input" value={form.name} onChange={sp('name')} placeholder="Mia Seo" />
                  </label>
                  <label className="admin-label">직함
                    <input className="admin-input" value={form.title} onChange={sp('title')} placeholder="Product Designer & Illustrator" />
                  </label>
                  <label className="admin-label">이메일
                    <input className="admin-input" value={form.email} onChange={sp('email')} placeholder="hello@example.com" />
                  </label>
                  <label className="admin-label">지역
                    <input className="admin-input" value={form.location} onChange={sp('location')} placeholder="Seoul, Korea — open to remote" />
                  </label>
                  <label className="admin-label" style={{ gridColumn: '1 / -1' }}>소개 문구
                    <textarea className="admin-textarea" rows={4} value={form.intro} onChange={sp('intro')} />
                  </label>
                </div>
              </section>

              <section className="admin-form-section">
                <div className="admin-section-header">
                  <h3 className="admin-form-section-title" style={{ margin: 0 }}>Services</h3>
                  <button className="admin-btn-add" onClick={addService}>+ 항목 추가</button>
                </div>
                {form.services.map((s, i) => (
                  <div key={i} className="admin-sec-image-row">
                    <input
                      className="admin-input"
                      style={{ flex: 1 }}
                      value={s}
                      onChange={(e) => updateService(i, e.target.value)}
                      placeholder="Product design — UX, UI, and systems"
                    />
                    <button className="admin-btn-remove" onClick={() => removeService(i)}>×</button>
                  </div>
                ))}
              </section>

              <section className="admin-form-section">
                <div className="admin-section-header">
                  <h3 className="admin-form-section-title" style={{ margin: 0 }}>Links</h3>
                  <button className="admin-btn-add" onClick={addLink}>+ 링크 추가</button>
                </div>
                {form.links.map((link, i) => (
                  <div key={i} className="admin-sec-image-row">
                    <input
                      className="admin-input"
                      style={{ flex: 1 }}
                      value={link.label}
                      onChange={(e) => updateLink(i, 'label', e.target.value)}
                      placeholder="LinkedIn"
                    />
                    <input
                      className="admin-input"
                      style={{ flex: 2 }}
                      value={link.href}
                      onChange={(e) => updateLink(i, 'href', e.target.value)}
                      placeholder="https://..."
                    />
                    <button className="admin-btn-remove" onClick={() => removeLink(i)}>×</button>
                  </div>
                ))}
              </section>
            </>
          )}

          <div className="admin-form-footer">
            {error && <p className="admin-error">{error}</p>}
            <button onClick={handleSave} disabled={saving || loading || notFound} className="admin-btn-primary">
              {saving ? '저장 중…' : '저장'}
            </button>
          </div>
        </main>
      </div>
    </AdminGuard>
  );
}
